import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Tooltip from "bootstrap/js/dist/tooltip";
import { createPortal } from "react-dom";

const MAX_LABEL_LENGTH = 15;

function referenceTitle(reference) {
  return reference.document_name || reference.filename || "Reference";
}

function truncateLabel(value) {
  const text = value.toString().trim();
  if (text.length <= MAX_LABEL_LENGTH) return text;

  return `${text.slice(0, MAX_LABEL_LENGTH).trimEnd()}...`;
}

function referenceMeta(reference) {
  return [reference.source_label, reference.source_owner].filter(Boolean).join(" · ");
}

function referenceKey(reference, index = null) {
  if (reference.url) return `web:${reference.url}`;
  if (reference.document_id) return `document:${reference.document_id}`;
  if (reference.organization_file_id) return `organization_file:${reference.organization_file_id}`;
  if (reference.product_knowledge_file_id) return `product_knowledge_file:${reference.product_knowledge_file_id}`;
  if (reference.file_id) return `file:${reference.file_id}`;

  return `reference:${index}`;
}

function scoreValue(reference) {
  const score = Number.parseFloat(reference.score);
  return Number.isNaN(score) ? Number.NEGATIVE_INFINITY : score;
}

function deduplicateReferences(references) {
  const uniqueReferences = new Map();

  references.forEach((reference, index) => {
    const key = referenceKey(reference, index);
    const existingReference = uniqueReferences.get(key);

    if (!existingReference || scoreValue(reference) > scoreValue(existingReference)) {
      uniqueReferences.set(key, reference);
    }
  });

  return Array.from(uniqueReferences.values());
}

function tooltipPosition(rect) {
  const tooltipWidth = 272;
  const maxLeft = Math.max(8, window.innerWidth - tooltipWidth);

  return {
    left: Math.min(Math.max(rect.left, 8), maxLeft),
    top: rect.bottom + 8
  };
}

function referenceTooltipText(reference) {
  return [referenceTitle(reference), referenceMeta(reference)].filter(Boolean).join(" · ");
}

function ReferenceLink({ reference, className, children, onClick = null, ...props }) {
  const url = reference.open_url || undefined;

  if (!url) {
    return (
      <span className={className} tabIndex={0} aria-label={referenceTitle(reference)} {...props}>
        {children}
      </span>
    );
  }

  return (
    <a
      className={className}
      href={url}
      target="_blank"
      rel="noreferrer"
      aria-label={referenceTitle(reference)}
      onClick={onClick}
      {...props}
    >
      {children}
    </a>
  );
}

function Bubble({ reference, measure = false, onClick = null }) {
  const tooltipText = referenceTooltipText(reference);

  return (
    <ReferenceLink
      reference={reference}
      className={`chatbot-reference-bubble chatbot-reference-bubble--${reference.source_type || "unknown"}${measure ? " chatbot-reference-bubble--measure" : ""}`}
      data-bs-toggle={measure ? undefined : "tooltip"}
      data-bs-placement={measure ? undefined : "top"}
      title={measure ? undefined : tooltipText}
      onClick={onClick}
    >
      {truncateLabel(referenceTitle(reference))}
    </ReferenceLink>
  );
}

export default function ChatbotReferenceBubbles({ references = [] }) {
  const uniqueReferences = deduplicateReferences(references);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const tooltipRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(uniqueReferences.length);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [overflowRect, setOverflowRect] = useState(null);
  const hiddenReferences = uniqueReferences.slice(visibleCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || uniqueReferences.length === 0) return undefined;

    const calculateVisibleCount = () => {
      const availableWidth = container.clientWidth;
      const bubbleNodes = Array.from(measure.querySelectorAll(".chatbot-reference-bubble--measure:not(.chatbot-reference-bubble--overflow)"));
      const overflowNode = measure.querySelector(".chatbot-reference-bubble--overflow");
      const gap = 6;
      const bubbleWidths = bubbleNodes.map((node) => node.offsetWidth);
      const overflowWidth = overflowNode?.offsetWidth || 0;
      const fullWidth = bubbleWidths.reduce((sum, width) => sum + width, 0) + gap * Math.max(bubbleWidths.length - 1, 0);

      if (fullWidth <= availableWidth) {
        setVisibleCount(uniqueReferences.length);
        return;
      }

      let usedWidth = overflowWidth;
      let nextVisibleCount = 0;

      for (const width of bubbleWidths) {
        const nextWidth = usedWidth + gap + width;
        if (nextWidth > availableWidth) break;

        usedWidth = nextWidth;
        nextVisibleCount += 1;
      }

      setVisibleCount(Math.max(nextVisibleCount, 0));
    };

    calculateVisibleCount();

    const resizeObserver = new ResizeObserver(calculateVisibleCount);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [references]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const tooltips = Array.from(containerRef.current.querySelectorAll('[data-bs-toggle="tooltip"]')).map(
      (element) => new Tooltip(element, { container: "body", boundary: "viewport" })
    );

    return () => tooltips.forEach((tooltip) => tooltip.dispose());
  }, [references, visibleCount]);

  useEffect(() => {
    if (!overflowOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (tooltipRef.current?.contains(event.target) || containerRef.current?.contains(event.target)) return;

      setOverflowOpen(false);
    };
    const closeOnOutsideFocus = (event) => {
      if (tooltipRef.current?.contains(event.target) || containerRef.current?.contains(event.target)) return;

      setOverflowOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOverflowOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("focusin", closeOnOutsideFocus);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("focusin", closeOnOutsideFocus);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [overflowOpen]);

  if (uniqueReferences.length === 0) return null;

  const openOverflow = (element) => {
    setOverflowRect(element.getBoundingClientRect());
    setOverflowOpen(true);
  };

  const toggleOverflow = (element) => {
    setOverflowRect(element.getBoundingClientRect());
    setOverflowOpen((currentValue) => !currentValue);
  };

  return (
    <div className="chatbot-reference-bubbles-wrap">
      <div className="chatbot-reference-bubbles" ref={containerRef}>
        {hiddenReferences.length > 0 ? (
          <button
            type="button"
            className="chatbot-reference-bubble chatbot-reference-bubble--overflow"
            aria-expanded={overflowOpen}
            onClick={(event) => toggleOverflow(event.currentTarget)}
            onMouseEnter={(event) => openOverflow(event.currentTarget)}
          >
            ...
          </button>
        ) : null}
        {uniqueReferences.slice(0, visibleCount).map((reference, index) => (
          <Bubble key={referenceKey(reference, index)} reference={reference} />
        ))}
      </div>

      {overflowOpen && overflowRect && hiddenReferences.length > 0 ? createPortal(
        <div
          className="chatbot-reference-tooltip chatbot-reference-tooltip--overflow"
          ref={tooltipRef}
          role="tooltip"
          style={tooltipPosition(overflowRect)}
        >
          {hiddenReferences.map((reference, index) => (
            <ReferenceLink
              key={referenceKey(reference, index)}
              reference={reference}
              className="chatbot-reference-tooltip__item"
              onClick={() => setOverflowOpen(false)}
            >
              <span className={`chatbot-reference-tooltip__badge chatbot-reference-tooltip__badge--${reference.source_type || "unknown"}`}>
                {reference.source_label || "Reference"}
              </span>
              <span className="chatbot-reference-tooltip__title">{referenceTitle(reference)}</span>
              {referenceMeta(reference) ? <span className="chatbot-reference-tooltip__meta">{referenceMeta(reference)}</span> : null}
            </ReferenceLink>
          ))}
        </div>,
        document.body
      ) : null}

      <div className="chatbot-reference-bubbles-measure" ref={measureRef} aria-hidden="true">
        <span className="chatbot-reference-bubble chatbot-reference-bubble--overflow chatbot-reference-bubble--measure">...</span>
        {uniqueReferences.map((reference, index) => (
          <Bubble key={`${referenceKey(reference, index)}-measure`} reference={reference} measure />
        ))}
      </div>
    </div>
  );
}
